import DataTable from '@/common/components/data-table/DataTable.tsx';
import AppErrorFallback from '@/common/components/error-state/AppErrorFallback.tsx';
import LightListFeaturePage from '@/common/components/list-route/LightListFeaturePage.tsx';
import {
  getDefaultColumnOrder,
  getDefaultColumnVisibility,
} from '@/common/utils/data-table.utils.ts';
import { getColumns } from '@/features/admin/users/columns.tsx';
import AdminNotificationPreferencesModal from '@/features/admin/users/components/AdminNotificationPreferencesModal.tsx';
import useUsers from '@/features/admin/users/hooks/useUsers.tsx';
import { getQueryErrorEventId } from '@/main.tsx';
import { BellIcon } from '@phosphor-icons/react';
import { Box, Button, Group } from '@mantine/core';
import { modals } from '@mantine/modals';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';

export const Route = createFileRoute('/admin/users')({
  component: RouteComponent,
});

function RouteComponent() {
  const usersQuery = useUsers();

  const columns = useMemo(() => getColumns(), []);
  const defaultVisibleColumns = useMemo(
    () => getDefaultColumnVisibility(columns),
    [columns],
  );
  const defaultColumnOrder = useMemo(() => getDefaultColumnOrder(columns), [columns]);

  if (usersQuery.isError) {
    return (
      <Box p="sm">
        <AppErrorFallback
          title="Failed to load users"
          message="We couldn't load users right now. Try again or report this issue."
          eventId={getQueryErrorEventId(['users'])}
          onRetry={() => usersQuery.refetch()}
          onReload={() => window.location.reload()}
          onBack={() => window.history.back()}
        />
      </Box>
    );
  }

  return (
    <LightListFeaturePage
      title="Users"
      subtitle="Manage user access and role assignments."
      action={
        <Group gap="sm">
          <Button
            variant="default"
            leftSection={<BellIcon size={16} />}
            onClick={() =>
              modals.open({
                title: 'Bulk Notification Preferences',
                size: 'lg',
                children: (
                  <AdminNotificationPreferencesModal
                    mode="bulk"
                    users={usersQuery.data?.users ?? []}
                  />
                ),
              })
            }
          >
            Bulk Preferences
          </Button>
        </Group>
      }
    >
      <DataTable
        columns={columns}
        columnVisibility={defaultVisibleColumns}
        columnOrder={defaultColumnOrder}
        data={usersQuery.data?.users ?? []}
        loading={usersQuery.isLoading}
        density="warmSpacious"
      />
    </LightListFeaturePage>
  );
}
